import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { setToken, getToken } from '../api/client';
import { useIdleTimer } from '../hooks/useIdleTimer';
import IdleWarningModal from '../components/IdleWarningModal';
import UpsellModal from '../components/UpsellModal';
import { toast } from '../hooks/use-toast';

const AuthContext = createContext(null);

// Idle timeout config — overridable at build time via .env.
// We apply sensible production defaults: 30 min total, 60s warning.
const IDLE_MIN = Number(process.env.REACT_APP_IDLE_TIMEOUT_MIN || 30);
const WARN_SEC = Number(process.env.REACT_APP_IDLE_WARN_SEC || 60);

// Vague 97.13 — Acting Org context (super_admin can adopt the context of an OF)
const ACTING_KEY = 'ifaap_acting_org';
const _readActing = () => {
  try { const raw = sessionStorage.getItem(ACTING_KEY); return raw ? JSON.parse(raw) : null; }
  catch { return null; }
};
const _writeActing = (data) => {
  try {
    if (data) sessionStorage.setItem(ACTING_KEY, JSON.stringify(data));
    else sessionStorage.removeItem(ACTING_KEY);
  } catch { /* noop */ }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingOrg, setActingOrgState] = useState(_readActing); // { org_id, name }

  const checkAuth = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Acting org setters (used by SuperAdmin > "Accéder à cet OF")
  const setActingOrg = useCallback((data) => {
    _writeActing(data);
    setActingOrgState(data);
  }, []);
  const clearActingOrg = useCallback(() => {
    _writeActing(null);
    setActingOrgState(null);
  }, []);

  const logout = useCallback(async (opts = {}) => {
    try { await api.post('/auth/logout'); } catch (e) { /* ignore */ }
    setToken(null);
    setUser(null);
    _writeActing(null);
    setActingOrgState(null);
    if (opts.reason === 'idle') {
      toast({
        title: 'Session expirée',
        description: 'Vous avez été déconnecté après une période d\'inactivité.',
      });
    }
  }, []);

  // ---- Idle timer (only when authenticated) -----------------------------
  const { phase, remaining, reset } = useIdleTimer({
    idleMinutes: IDLE_MIN,
    warnSeconds: WARN_SEC,
    enabled: !!user,
    onTimeout: () => { logout({ reason: 'idle' }); },
  });

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, actingOrg, setActingOrg, clearActingOrg, idle: { phase, remaining, reset } }}>
      {children}
      <IdleWarningModal
        open={!!user && phase === 'warning'}
        remaining={remaining}
        onStay={reset}
        onLogout={() => logout({ reason: 'manual' })}
      />
      <UpsellModal />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
